<template>
    <div class="selector-wrapper" :class="{ mobile: getMobileAppState}">
        <dropdown-account
        :params="{ headerType: 'description-header' }"
        :options="options"
        :selectedOption="selectedOption"
        @clicked="emit('clicked', $event)"/>
        <!-- Balances -->
        <div v-if="creditInfo && !spinnerActive" v-dragscroll="getMobileAppState" class="text-blocks-container">
            <!-- Loan initial value -->
            <text-block :params="getLoanInitialValueParams" :applyEllipsis="!getMobileAppState"/>
        </div>
        <!-- Error-state -->
        <div v-if="!creditInfo && !spinnerActive" class="error-wrapper"><icon-error/></div>
        <!-- Spinner -->
        <spinner v-if="spinnerActive" isSmall/>
        <!-- Gradient for overflow @mobile -->
        <div v-if="getMobileAppState" class="mobile-gradient"/>
    </div>
</template>

<script setup>
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import TextBlock from '@/components/generic/textBlocks/Generic'
import IconError from '@/assets/icons/error.svg?inline'
import Spinner from '@/components/generic/loaders/Generic'
/* Vue */
import { computed } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* Aux */
import { amountFormat } from '@/assets/scripts/amounts'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
/* mapGetters */
const { getMobileAppState } = mapGetters()
/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@selectedOption { String, Number }
@options { Array } each item is an Object
	- id { String, Number }
	- accountName { String }
	- accountNumber { String }
	- accountBalance { Number }
	- accountCurrency { String }
@creditInfo { Object }
    - id { String, Number }
    - loanInitialValue { Number }
	- loanLeftToPay { Number }
	- accountCurrency { String }
@spinnerActive
*/
const props = defineProps({
	selectedOption: [String, Number],
	options: Array,
    creditInfo: Object,
    spinnerActive: Boolean
})

const getLoanInitialValueParams = computed(() => {
    return {
        label: t('selectorWithDetails.credits.totalAmount'),
        content: amountFormat(Math.abs(props.creditInfo.loanInitialValue), props.creditInfo.accountCurrency)
    }
})

const getLoanLeftToPayParams = computed(() => {
    return {
        label: t('selectorWithDetails.credits.debtAmount'),
        content: amountFormat(Math.abs(props.creditInfo.loanLeftToPay), props.creditInfo.accountCurrency)
    }
})
</script>

<style lang="scss" scoped>
.selector-wrapper {
    height: 64px;
    width: 365px;

    display: flex;
    flex-direction: row;
    align-items: center;

    border-radius: 16px;
    background-color: var(--secondary-light-4);
    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);

    .dropdown-wrapper {
        width: 209px;

        :deep(.dropdown-animation-wrapper) {
            width: 292px;
            max-width: 100%;
            left: -20px;
        }
    }

    :deep(.spinner-wrapper), .text-blocks-container, .error-wrapper {
        flex-grow: 1;
        padding-left: 16px;
        border-left: 1px solid var(--secondary-light-2);
    }

    :deep(.spinner-wrapper) {
        width: auto;
        display: flex;
        justify-content: center;

        padding-left: 0;
    }

    .text-blocks-container {
        display: flex;
        gap: 16px;
        align-items: center;
        flex-grow: 1;
        padding-right: 16px;

        .divider {
            height: calc(100% - 28px);
            width: 1px;
            background-color: var(--secondary-light-2);
        }

        .text-blocks-wrapper {
            :deep(label) {
                @include H8-Regular;
                line-height: 13px
            }

            :deep(.content) {
                @include H6-Medium;
                color: var(--secondary-dark-2);
            }

            :deep(.content-wrapper) {
                margin-top: 0;
                line-height: 18px;
                gap: 0;

                svg {
                    width: 12px;
                    height: 12px;
                }
            }
        }
    }

    .error-wrapper {
        height: 100%;

        display: flex;
        align-items: center;
        justify-content: center;

        :deep(.change-color) {
            stroke: var(--negative);
        }
    }

    // MOBILE STYLES
    &.mobile {
        height: 126px;
        width: 100%;
        padding: 0;
        gap: 0;

        position: relative;
        flex-direction: column;

        .dropdown-wrapper,
        .text-blocks-container,
        .error-wrapper,
        :deep(.spinner-wrapper) {
            width: 100%;
            height: 64px;
            align-items: center;
        }

        .dropdown-wrapper {
            justify-content: center;
            border-bottom: 1px solid var(--secondary-light-2);

            :deep(.dropdown-animation-wrapper) {
                width: 292px;
                max-width: 100%;
                left: unset;
                right: 8px;
            }
        }

        .text-blocks-container {
            padding-left: 16px;
            border-left: none;

            .text-blocks-wrapper {
                width: auto;
                height: auto;

                :deep(.content) {
                    white-space: nowrap;
                }

                &:last-of-type {
                    padding-right: 30px;
                }
            }
        }

        .mobile-gradient {
            height: 64px;
            width: 44px;

            position: absolute;
            bottom: 0;
            right: 0;

            border-radius: 16px;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, var(--white) 100%)
        }
    }
    // DROPDOWN ACCOUNTS STYLES
    :deep(.header) {
        border-radius: 16px;
    }
}
</style>
